import { getBrowser } from "@/utils/browser";

/**
 * App 用 localStorage，PC 用 sessionStorage（与 open-account 一致）
 */
export function getPrivateFundToken() {
  return getBrowser().isApp
    ? localStorage.getItem("token")
    : sessionStorage.getItem("token");
}

/**
 * 根据收益率文案返回涨跌样式 class（红涨绿跌）
 */
export function getReturnTrendClass(value) {
  if (value === "" || value == null) {
    return "";
  }
  const num = parseFloat(String(value).replace(/%/g, ""));
  if (Number.isNaN(num) || num === 0) {
    return "is-flat";
  }
  return num > 0 ? "is-rise" : "is-fall";
}

/**
 * 将 API 收益率格式化为 UI 展示文案（如 +1.8300%）
 */
export function formatReturnRate(value) {
  if (value === "" || value == null) {
    return "--";
  }
  const num = parseFloat(String(value).replace(/%/g, ""));
  if (Number.isNaN(num)) {
    return String(value);
  }
  const prefix = num > 0 ? "+" : "";
  return `${prefix}${num}%`;
}

export const AUM_UNIT_BILLION = 1;
export const AUM_UNIT_MILLION = 2;

export function formatAssetsUnderManagement(value) {
  if (value === "" || value == null) {
    return "--";
  }
  const num = parseFloat(String(value));
  if (Number.isNaN(num)) {
    return String(value);
  }
  return num.toFixed(4);
}

export function normalizeAssetsUnderManagementUnit(unit) {
  const n = Number(unit);
  if (Number.isNaN(n)) {
    return AUM_UNIT_BILLION;
  }
  return n;
}

export function resolveAssetsUnderManagementLabel(translate, unit, unitDisplay) {
  const n = normalizeAssetsUnderManagementUnit(unit);
  const display = unitDisplay != null ? String(unitDisplay).trim() : "";

  if (n === AUM_UNIT_BILLION) {
    return translate("privateFund.list.assetsUnderManagementBillion");
  }
  if (n === AUM_UNIT_MILLION) {
    return translate("privateFund.list.assetsUnderManagementMillion");
  }
  if (display) {
    return translate("privateFund.list.assetsUnderManagement", { unit: display });
  }
  return translate("privateFund.list.assetsUnderManagementPlain");
}

/**
 * 基金列表 API 字段 → 列表卡片字段
 */
export function mapFundListItem(raw) {
  return {
    id: raw.id,
    name: raw.product_name || "",
    assetsUnderManagement: formatAssetsUnderManagement(raw.assets_under_management),
    assetsUnderManagementUnit: normalizeAssetsUnderManagementUnit(
      raw.assets_under_management_unit
    ),
    assetsUnderManagementUnitDisplay: raw.assets_under_management_unit_display || "",
    returnYtd: formatReturnRate(raw.ytd_return),
    returnSinceInception: formatReturnRate(raw.since_inception_return),
    strategy: raw.investment_strategy || "",
    navList: Array.isArray(raw.nav_list) ? raw.nav_list : []
  };
}

function pickPdfUrl(plainUrl, signUrl) {
  return signUrl || plainUrl || "";
}

function formatDetailField(value) {
  if (value === "" || value == null) {
    return "--";
  }
  return String(value);
}

export function parseAmountValue(value) {
  if (value === "" || value == null) {
    return null;
  }
  const num = parseFloat(String(value).replace(/[^\d.]/g, ""));
  return Number.isNaN(num) ? null : num;
}

export function formatSubscriptionAmountValue(value) {
  const num = parseAmountValue(value);
  if (num == null) {
    return "";
  }
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

export function getSubscriptionAmountStep(minSubscription, additionalSubscription) {
  return (
    parseAmountValue(additionalSubscription) ||
    parseAmountValue(minSubscription) ||
    0
  );
}

function toAmountCents(value) {
  const num = parseAmountValue(value);
  if (num == null) {
    return null;
  }
  return Math.round(num * 100);
}

export function isSubscriptionAmountStepValid(
  amount,
  minSubscription,
  additionalSubscription
) {
  const amountValue = parseAmountValue(amount);
  const minAmount = parseAmountValue(minSubscription);
  const step = getSubscriptionAmountStep(minSubscription, additionalSubscription);
  if (amountValue == null || amountValue <= 0) {
    return false;
  }
  if (minAmount != null && amountValue < minAmount) {
    return false;
  }
  if (!step || step <= 0) {
    return true;
  }
  const amountCents = toAmountCents(amountValue);
  const stepCents = toAmountCents(step);
  if (amountCents == null || stepCents == null || stepCents <= 0) {
    return true;
  }
  if (minAmount == null) {
    return amountCents % stepCents === 0;
  }
  const minCents = toAmountCents(minAmount);
  if (minCents == null) {
    return true;
  }
  const diffCents = amountCents - minCents;
  if (diffCents < 0) {
    return false;
  }
  return diffCents % stepCents === 0;
}

export function normalizeSubscriptionAmount(
  amount,
  minSubscription,
  additionalSubscription
) {
  const amountValue = parseAmountValue(amount);
  if (amountValue == null || amountValue <= 0) {
    return "";
  }
  const minAmount = parseAmountValue(minSubscription);
  const step = getSubscriptionAmountStep(minSubscription, additionalSubscription);
  if (minAmount == null || !step || step <= 0) {
    return formatSubscriptionAmountValue(amountValue);
  }

  let normalized = amountValue;
  if (normalized < minAmount) {
    normalized = minAmount;
  } else {
    const diff = normalized - minAmount;
    const stepCount = Math.round(diff / step);
    normalized = minAmount + stepCount * step;
    normalized = Math.round(normalized * 100) / 100;
    if (normalized < minAmount) {
      normalized = minAmount;
    }
  }
  return formatSubscriptionAmountValue(normalized);
}

export function getAgreementStorageKey(id) {
  return `privateFundAgreementUrls_${id}`;
}

/**
 * 基金详情 API 字段 → 详情页 UI + 认购协议 PDF（字段名与接口保持一致）
 */
export function mapFundDetail(raw) {
  return {
    detail: {
      id: raw.id,
      product_name: formatDetailField(raw.product_name),
      assets_under_management: formatAssetsUnderManagement(raw.assets_under_management),
      assets_under_management_unit: normalizeAssetsUnderManagementUnit(
        raw.assets_under_management_unit
      ),
      assets_under_management_unit_display: raw.assets_under_management_unit_display || "",
      ytd_return: formatReturnRate(raw.ytd_return),
      since_inception_return: formatReturnRate(raw.since_inception_return),
      fund_company: formatDetailField(raw.fund_company),
      currency_display: formatDetailField(raw.currency_display),
      subscription_day: formatDetailField(raw.subscription_day),
      subscription_price_display: formatDetailField(raw.subscription_price_display),
      min_subscription_amount: formatDetailField(raw.min_subscription_amount),
      additional_subscription_amount: formatDetailField(raw.additional_subscription_amount),
      share_lock_up: formatDetailField(raw.share_lock_up),
      redemption_day: formatDetailField(raw.redemption_day),
      min_redemption: formatDetailField(raw.min_redemption),
      min_holding: formatDetailField(raw.min_holding),
      subscription_fee: formatDetailField(raw.subscription_fee),
      redemption_fee: formatDetailField(raw.redemption_fee),
      management_fee: formatDetailField(raw.management_fee),
      investment_strategy: raw.investment_strategy || ""
    },
    agreementUrls: {
      terms: pickPdfUrl(raw.terms_list_url, raw.terms_list_url_sign),
      memo: pickPdfUrl(raw.ppm_url, raw.ppm_url_sign),
      appendix: pickPdfUrl(raw.appendix_url, raw.appendix_url_sign)
    }
  };
}

/** 订单状态码 → 列表状态样式（接口字段 status：0已提交 1已受理 2已拒绝） */
const ORDER_STATUS_CLASS_BY_CODE = {
  0: "is-submitted",
  1: "is-accepted",
  2: "is-rejected"
};

function formatOrderAmount(value) {
  if (value === "" || value == null) {
    return "--";
  }
  const raw = String(value).replace(/,/g, "");
  const num = parseFloat(raw);
  if (Number.isNaN(num)) {
    return String(value);
  }
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/** 认购时间展示为 YY/MM/DD */
export function formatOrderDate(value) {
  if (value === "" || value == null) {
    return "--";
  }
  const text = String(value).trim();
  const matched = text.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (matched) {
    const yy = matched[1].slice(-2);
    const mm = matched[2].padStart(2, "0");
    const dd = matched[3].padStart(2, "0");
    return `${yy}/${mm}/${dd}`;
  }
  if (/^\d{2}\/\d{2}\/\d{2}$/.test(text)) {
    return text;
  }
  return text;
}

/**
 * 基金订单 API 字段 → 列表行字段
 * @see GET /api/fund_product/order/list
 */
export function mapFundOrderItem(raw) {
  return {
    id: raw.id,
    orderNo: raw.acceptance_sn || String(raw.id || ""),
    productName: raw.product_name || "",
    fundProductId: raw.fund_product_id,
    amount: formatOrderAmount(raw.subscription_amount),
    currency: raw.currency_display || "",
    status: Number(raw.status),
    statusText: raw.status_name || "--",
    createdAt: formatOrderDate(raw.created_at)
  };
}

export function getOrderStatusClass(status) {
  return ORDER_STATUS_CLASS_BY_CODE[Number(status)] || "is-submitted";
}

const NAV_CHART_WIDTH = 300;
const NAV_CHART_HEIGHT = 160;
const NAV_CHART_PADDING_TOP = 12;
const NAV_CHART_PADDING_BOTTOM = 12;
const NAV_CHART_PADDING_HORIZONTAL = 8;
const NAV_CHART_Y_AXIS_WIDTH = 84;
const NAV_CHART_MAX_Y_TICKS = 5;
/** X 轴统一目标标签数（点数不足时显示全部），避免有的 3 个有的 6 个 */
const NAV_CHART_TARGET_X_LABELS = 5;
/** DD/MM 标签约占图宽 12%~14%，用百分比下限兜底防重叠 */
const NAV_CHART_MIN_X_LABEL_GAP_PERCENT = 15;

export function buildNavChartLinePath(points) {
  if (!Array.isArray(points) || !points.length) {
    return "";
  }
  return points
    .reduce((path, point, index) => {
      const segment = `${index === 0 ? "M" : "L"}${point.x},${point.y}`;
      return `${path} ${segment}`;
    }, "")
    .trim();
}

export function buildNavChartAreaPath(points, chartHeight = NAV_CHART_HEIGHT) {
  if (!Array.isArray(points) || !points.length) {
    return "";
  }
  const linePath = buildNavChartLinePath(points);
  const lastPoint = points[points.length - 1];
  return `${linePath} L${lastPoint.x},${chartHeight} L0,${chartHeight} Z`;
}

function createNavChartEmptyState() {
  return {
    points: [],
    axisLabels: [],
    yAxisLabels: [],
    gridLines: [],
    chartWidth: NAV_CHART_WIDTH,
    chartHeight: NAV_CHART_HEIGHT,
    plotTop: NAV_CHART_PADDING_TOP,
    plotBottom: NAV_CHART_HEIGHT - NAV_CHART_PADDING_BOTTOM,
    yAxisWidth: NAV_CHART_Y_AXIS_WIDTH
  };
}

function valueToNavChartY(val, minVal, maxVal, plotTop, plotBottom) {
  const range = maxVal - minVal || 1;
  const normalized = (val - minVal) / range;
  const plotHeight = plotBottom - plotTop;
  return plotBottom - normalized * plotHeight;
}

function buildEvenlySpacedTicks(minVal, maxVal, tickCount) {
  if (tickCount <= 1) {
    return [minVal];
  }
  const ticks = [];
  for (let i = 0; i < tickCount; i += 1) {
    ticks.push(minVal + (i / (tickCount - 1)) * (maxVal - minVal));
  }
  return ticks;
}

function resolveNavChartYAxis(minVal, maxVal, tickCount = NAV_CHART_MAX_Y_TICKS) {
  if (minVal === maxVal) {
    return {
      domainMin: minVal,
      domainMax: maxVal,
      tickValues: [minVal]
    };
  }

  return {
    domainMin: minVal,
    domainMax: maxVal,
    tickValues: buildEvenlySpacedTicks(minVal, maxVal, tickCount)
  };
}

/** 从接口 nav_per_share 字符串推断展示用小数位数 */
function inferNavValueDecimalPlaces(navList) {
  let maxDecimals = 0;
  (navList || []).forEach(item => {
    const raw = String(item?.nav_per_share ?? "").trim();
    const matched = raw.match(/\.(\d+)/);
    if (matched) {
      maxDecimals = Math.max(maxDecimals, matched[1].length);
    }
  });
  return maxDecimals;
}

function formatNavChartAxisValue(value, decimalPlaces) {
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  if (decimalPlaces <= 0) {
    return String(Math.round(num));
  }
  return num.toFixed(decimalPlaces);
}

/** 净值展示：直接使用接口原值 */
function formatNavPerShareDisplay(rawValue) {
  if (rawValue === "" || rawValue == null) {
    return "--";
  }
  return String(rawValue).trim();
}

export function formatNavChartTooltipValue(value) {
  return formatNavPerShareDisplay(value);
}

function buildNavChartYAxisLabels(
  tickValues,
  domainMin,
  domainMax,
  plotTop,
  plotBottom,
  decimalPlaces
) {
  const count = tickValues.length;
  return tickValues.map((value, index) => {
    let y = valueToNavChartY(value, domainMin, domainMax, plotTop, plotBottom);
    if (index === 0) {
      y = plotBottom;
    } else if (index === count - 1) {
      y = plotTop;
    }
    return {
      label: formatNavChartAxisValue(value, decimalPlaces),
      y,
      position: "middle"
    };
  });
}

function resolveNavChartPointX(index, dataLength) {
  const pad = NAV_CHART_PADDING_HORIZONTAL;
  const plotWidth = NAV_CHART_WIDTH - pad * 2;
  if (dataLength <= 1) {
    return NAV_CHART_WIDTH / 2;
  }
  return pad + (index / (dataLength - 1)) * plotWidth;
}

/**
 * X 轴标签数量：默认目标 5 个；
 * 若点太密（相邻点间距 < 最小可视间距）则自动降到还能铺开的最大个数，避免 3/6 忽多忽少。
 */
function resolveNavChartLabelCount(dataLength, targetLabels = NAV_CHART_TARGET_X_LABELS) {
  if (dataLength <= 1) {
    return dataLength;
  }
  const plotSpanPercent =
    ((NAV_CHART_WIDTH - NAV_CHART_PADDING_HORIZONTAL * 2) / NAV_CHART_WIDTH) * 100;
  const stepPercent = plotSpanPercent / (dataLength - 1);
  const minIndexGap = Math.max(
    1,
    Math.ceil(NAV_CHART_MIN_X_LABEL_GAP_PERCENT / stepPercent)
  );
  const maxByGap = 1 + Math.floor((dataLength - 1) / minIndexGap);
  return Math.min(targetLabels, dataLength, maxByGap);
}

/**
 * 选取严格递增且尽量等距的数据下标。
 */
function pickNavChartAxisIndices(dataLength, labelCount) {
  const lastIdx = dataLength - 1;
  if (labelCount >= dataLength) {
    return Array.from({ length: dataLength }, (_, i) => i);
  }
  const indices = [];
  for (let i = 0; i < labelCount; i += 1) {
    indices.push(Math.round((i * lastIdx) / (labelCount - 1)));
  }
  indices[0] = 0;
  indices[labelCount - 1] = lastIdx;
  for (let i = 1; i < labelCount - 1; i += 1) {
    const minAllowed = indices[i - 1] + 1;
    const maxAllowed = lastIdx - (labelCount - 1 - i);
    indices[i] = Math.min(Math.max(indices[i], minAllowed), maxAllowed);
  }
  return indices;
}

/**
 * 按 xPercent 最小间距筛掉会重叠的中间标签，首尾始终保留。
 */
function thinNavChartXAxisLabels(labels, minGapPercent = NAV_CHART_MIN_X_LABEL_GAP_PERCENT) {
  if (labels.length <= 2) {
    return labels;
  }
  const first = labels[0];
  const last = labels[labels.length - 1];
  const kept = [first];
  for (let i = 1; i < labels.length - 1; i += 1) {
    const prev = kept[kept.length - 1];
    if (labels[i].xPercent - prev.xPercent >= minGapPercent) {
      kept.push(labels[i]);
    }
  }
  while (
    kept.length > 1 &&
    last.xPercent - kept[kept.length - 1].xPercent < minGapPercent
  ) {
    kept.pop();
  }
  kept.push(last);
  return kept;
}

function pickNavChartXAxisLabels(navList, targetLabels = NAV_CHART_TARGET_X_LABELS) {
  const dataLength = navList.length;
  if (!dataLength) {
    return [];
  }

  if (dataLength === 1) {
    return [
      {
        label: formatNavChartAxisDateShort(navList[0].date),
        xPercent: 50,
        align: "center"
      }
    ];
  }

  const labelCount = resolveNavChartLabelCount(dataLength, targetLabels);
  const indices = pickNavChartAxisIndices(dataLength, labelCount);
  const labels = indices.map(dataIdx => {
    const pointX = resolveNavChartPointX(dataIdx, dataLength);
    const xPercent = (pointX / NAV_CHART_WIDTH) * 100;
    let align = "center";
    if (dataIdx === 0) {
      align = "start";
    } else if (dataIdx === dataLength - 1) {
      align = "end";
    }
    return {
      label: formatNavChartAxisDateShort(navList[dataIdx].date),
      xPercent,
      align
    };
  });

  return thinNavChartXAxisLabels(labels);
}

/** 统一格式化为 MM/DD/YYYY */
function formatNavChartAxisDate(dateStr) {
  const text = String(dateStr).trim();
  let matched = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (matched) {
    const mm = matched[1].padStart(2, "0");
    const dd = matched[2].padStart(2, "0");
    return `${mm}/${dd}/${matched[3]}`;
  }
  matched = text.match(/^(\d{4})[-/.](\d{1,2})(?:[-/.](\d{1,2}))?$/);
  if (matched) {
    const mm = matched[2].padStart(2, "0");
    const dd = (matched[3] || "01").padStart(2, "0");
    return `${mm}/${dd}/${matched[1]}`;
  }
  return text;
}

/** 横轴短日期：DD/MM */
function formatNavChartAxisDateShort(dateStr) {
  const full = formatNavChartAxisDate(dateStr);
  const matched = full.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (matched) {
    return `${matched[2]}/${matched[1]}`;
  }
  return full;
}

/**
 * 净值历史 nav_list → 走势 SVG 坐标与横轴/纵轴标签
 * 折线使用全量数据；Y 轴最多 5 个刻度；X 轴目标 5 个日期标签（点数不足则全显），首尾必含并落在折线顶点上，间距过滤防重叠
 * @see GET /api/fund_product/nav_history
 */
export function buildNavChartFromList(navList) {
  const empty = createNavChartEmptyState();
  if (!Array.isArray(navList) || !navList.length) {
    return empty;
  }

  const values = navList
    .map(item => parseFloat(String(item.nav_per_share)))
    .filter(val => !Number.isNaN(val));
  if (!values.length) {
    return empty;
  }

  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const plotTop = NAV_CHART_PADDING_TOP;
  const plotBottom = NAV_CHART_HEIGHT - NAV_CHART_PADDING_BOTTOM;
  const plotHeight = plotBottom - plotTop;
  const yAxis = resolveNavChartYAxis(minVal, maxVal);
  const { domainMin, domainMax } = yAxis;
  const yRange = domainMax - domainMin || 1;
  const dataLength = navList.length;

  const points = navList.map((item, index) => {
    const val = parseFloat(String(item.nav_per_share));
    const safeVal = Number.isNaN(val) ? minVal : val;
    const x = resolveNavChartPointX(index, dataLength);
    const normalized = (safeVal - domainMin) / yRange;
    const y = plotBottom - normalized * plotHeight;
    return {
      x,
      y,
      index,
      date: item.date,
      dateLabel: formatNavChartAxisDateShort(item.date),
      navPerShare: item.nav_per_share,
      navPerShareDisplay: formatNavPerShareDisplay(item.nav_per_share)
    };
  });

  const axisLabels = pickNavChartXAxisLabels(navList);
  const navDecimalPlaces = inferNavValueDecimalPlaces(navList);
  const yAxisLabels = buildNavChartYAxisLabels(
    yAxis.tickValues,
    domainMin,
    domainMax,
    plotTop,
    plotBottom,
    navDecimalPlaces
  );
  const gridLines = yAxisLabels.map(item => item.y);

  return {
    points,
    axisLabels,
    yAxisLabels,
    gridLines,
    chartWidth: NAV_CHART_WIDTH,
    chartHeight: NAV_CHART_HEIGHT,
    plotTop,
    plotBottom,
    yAxisWidth: NAV_CHART_Y_AXIS_WIDTH
  };
}

/** 根据点击/触摸的 viewBox x 坐标，取最近的数据点索引 */
export function findNearestNavChartPointIndex(points, x) {
  if (!Array.isArray(points) || !points.length) {
    return -1;
  }
  let nearest = 0;
  let minDist = Math.abs(points[0].x - x);
  for (let i = 1; i < points.length; i += 1) {
    const dist = Math.abs(points[i].x - x);
    if (dist < minDist) {
      minDist = dist;
      nearest = i;
    }
  }
  return nearest;
}

const NAV_CHART_TOOLTIP_EDGE_PERCENT = 24;

/** 图表浮窗定位：按数据点横向位置贴边对齐，避免浮窗被裁切 */
export function resolveNavChartTooltipStyle(point, chartWidth, chartHeight) {
  if (!point || !chartWidth || !chartHeight) {
    return {};
  }
  const leftPercent = (point.x / chartWidth) * 100;
  const topPercent = (point.y / chartHeight) * 100;
  const edgePercent = NAV_CHART_TOOLTIP_EDGE_PERCENT;

  let transformX = "-50%";
  if (leftPercent <= edgePercent) {
    transformX = "0";
  } else if (leftPercent >= 100 - edgePercent) {
    transformX = "-100%";
  }

  const showBelow = topPercent < 28;
  const transformY = showBelow ? "16px" : "calc(-100% - 16px)";

  return {
    left: `${leftPercent}%`,
    top: `${topPercent}%`,
    transform: `translate(${transformX}, ${transformY})`
  };
}
